import { createContext, use, useEffect, useMemo, useSyncExternalStore, type ReactNode } from "react"
import {
  forcedClass,
  keyboardInsetOf,
  keyboardShownOf,
  typesText,
  viewportQueries,
  type Viewport,
} from "./viewport.ts"

/*
 * `useViewport()`: the `compact`, `touch` and `standalone` variants as code reads them, for what a
 * class can't decide (which component renders, which sensor drags, whether a link opens in place).
 * It reads the browser as it renders, so the first render is already right and nothing flashes from
 * the desk's form to the phone's.
 */

/** One subscription per query, made once, so React sees the same function on every render. */
const subscribers = new Map<string, (changed: () => void) => () => void>()

function subscriberOf(query: string) {
  let subscribe = subscribers.get(query)
  if (!subscribe) {
    subscribe = (changed) => {
      const list = window.matchMedia(query)
      list.addEventListener("change", changed)
      return () => list.removeEventListener("change", changed)
    }
    subscribers.set(query, subscribe)
  }
  return subscribe
}

function useMedia(query: string) {
  return useSyncExternalStore(subscriberOf(query), () => window.matchMedia(query).matches)
}

type Forced = Partial<Pick<Viewport, "compact" | "touch">>

const ForcedViewport = createContext<Forced>({})

/** Whether the page is on its compact layout, under a coarse pointer, and in the installed app. */
export function useViewport(): Viewport {
  const forced = use(ForcedViewport)
  const compact = useMedia(viewportQueries.compact)
  const touch = useMedia(viewportQueries.touch)
  const standalone = useMedia(viewportQueries.standalone)
  return useMemo(
    () => ({ compact: forced.compact ?? compact, touch: forced.touch ?? touch, standalone }),
    [forced, compact, touch, standalone],
  )
}

/**
 * Forces `compact` or `touch` for a subtree, whatever the window is, so the kitchen sink can show a
 * composite's phone or touch form beside its desk form at desk width. Forcing one also makes the
 * subtree a `.compact` or `.touch` one, so its `compact:` or `touch:` classes follow too; the
 * phone's type scale needs both forced, as it needs both on a device. Forcing `false` reaches only
 * `useViewport()`, since CSS can't undo a media query. The kitchen sink is its only caller.
 */
export function ViewportOverride({ compact, touch, children }: Forced & { children: ReactNode }) {
  const forced = useMemo(() => ({ compact, touch }), [compact, touch])
  const classes = [compact && forcedClass.compact, touch && forcedClass.touch].filter(Boolean)
  return (
    <ForcedViewport value={forced}>
      {classes.length > 0 ? (
        <div className={["contents", ...classes].join(" ")}>{children}</div>
      ) : (
        children
      )}
    </ForcedViewport>
  )
}

/**
 * Keeps `--keyboard-inset` on `<html>` at how much of the page the on-screen keyboard covers, for
 * `bottom-keyboard`. Mounted once, in the shell.
 */
export function useKeyboardInset() {
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    const root = document.documentElement
    const measure = () =>
      root.style.setProperty(
        "--keyboard-inset",
        `${keyboardInsetOf(window.innerHeight, viewport)}px`,
      )
    measure()
    viewport.addEventListener("resize", measure)
    viewport.addEventListener("scroll", measure)
    return () => {
      viewport.removeEventListener("resize", measure)
      viewport.removeEventListener("scroll", measure)
      root.style.removeProperty("--keyboard-inset")
    }
  }, [])
}

/**
 * Hears focus move. Focus going from one field to the next leaves the first and reaches the second
 * in one task, so it is read once both have happened, and the answer never blinks in between.
 */
function subscribeFocus(changed: () => void) {
  const settled = () => queueMicrotask(changed)
  document.addEventListener("focusin", settled)
  document.addEventListener("focusout", settled)
  return () => {
    document.removeEventListener("focusin", settled)
    document.removeEventListener("focusout", settled)
  }
}

const focusTypesText = () => typesText(document.activeElement as HTMLInputElement | null)

/** The tallest the visual viewport has stood at each width, the page's height with no keyboard. */
const tallestAt = new Map<number, number>()

/** Hears the page resize: the visual viewport on iOS, the window too on Android. */
function subscribeResize(changed: () => void) {
  const viewport = window.visualViewport
  window.addEventListener("resize", changed)
  viewport?.addEventListener("resize", changed)
  return () => {
    window.removeEventListener("resize", changed)
    viewport?.removeEventListener("resize", changed)
  }
}

/**
 * Whether the page stands a keyboard short of its tallest at this width, the tallest kept as it is
 * seen. Height is taken unzoomed, so a pinch doesn't read as a keyboard. Without a visual viewport
 * there is nothing to measure, and focus alone decides.
 */
function pageShortened() {
  const viewport = window.visualViewport
  if (!viewport) return true
  const width = document.documentElement.clientWidth
  const height = viewport.height * viewport.scale
  const tallest = Math.max(tallestAt.get(width) ?? 0, height)
  tallestAt.set(width, tallest)
  return keyboardShownOf(tallest, height)
}

/**
 * Whether the on-screen keyboard is up: on touch, while focus is in something typed into and the
 * page stands a keyboard short of its full height. Asked by what gives the keyboard its room, as
 * the tab bar does by stepping aside. Focus alone isn't enough: Android's Back and an iPad's
 * hide-keyboard key put the keyboard away and leave the field focused. A page turned while the
 * keyboard is up has no full height yet at its new width, so it reads the keyboard as down until
 * the keyboard has once gone.
 */
export function useKeyboardUp() {
  const { touch } = useViewport()
  const typing = useSyncExternalStore(subscribeFocus, focusTypesText)
  const shortened = useSyncExternalStore(subscribeResize, pageShortened)
  return touch && typing && shortened
}
